import { FormHelperText, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel } from '@mui/material';
import { styled, css } from 'styled-components';
import theme from 'styled-theming';

import Icon from 'components/Icon';
import { styleBodyL, styleBodyM, styleBodyS, styleLabelM } from 'style/components/Typography';
import { MQ } from 'style/mediaQueries';

const labelColor = theme('theme', {
    undefined: '--text-on-neutral-secondary',
    lightgray: '--text-on-neutral-secondary',
    blue: '--text-on-blue-secondary',
    cyan: '--text-on-cyan-secondary',
    black: '--text-on-blue-secondary',
});

const disabledColor = theme('theme', {
    undefined: '--text-on-neutral-disabled',
    lightgray: '--text-on-neutral-disabled',
    blue: '--text-on-blue-disabled',
    cyan: '--text-on-cyan-disabled',
    black: '--text-on-blue-disabled',
});

const textColor = theme('theme', {
    undefined: '--text-on-neutral-primary',
    lightgray: '--text-on-neutral-primary',
    blue: '--text-on-blue-primary',
    cyan: '--text-on-cyan-primary',
    black: '--text-on-blue-primary',
});

const markedColor = theme('theme', {
    undefined: '--icon-on-neutral-primary',
    lightgray: '--icon-on-neutral-primary',
    blue: '--icon-on-blue-primary',
    cyan: '--icon-on-cyan-primary',
    black: '--icon-on-blue-primary',
});

const borderHover = theme('theme', {
    undefined: '--field-on-neutral-hover-border',
    lightgray: '--field-on-neutral-hover-border',
    blue: '--field-on-blue-hover-border',
    cyan: '--field-on-cyan-hover-border',
    black: '--field-on-blue-hover-border',
});

const borderColor = theme('theme', {
    undefined: '--field-on-neutral-default-border',
    lightgray: '--field-on-neutral-default-border',
    blue: '--field-on-blue-default-border',
    cyan: '--field-on-cyan-default-border',
    black: '--field-on-blue-default-border',
});

const borderDisabledColor = theme('theme', {
    undefined: '--field-on-neutral-disabled-border',
    lightgray: '--field-on-neutral-disabled-border',
    blue: '--field-on-blue-disabled-border',
    cyan: '--field-on-cyan-disabled-border',
    black: '--field-on-blue-disabled-border',
});

const borderError = theme('theme', {
    undefined: '--field-on-neutral-error-border',
    lightgray: '--field-on-neutral-error-border',
    blue: '--field-on-blue-error-border',
    cyan: '--field-on-cyan-error-border',
    black: '--field-on-blue-error-border',
});

export const Wrapper = styled(FormControl)`
    && {
        grid-column: col-start 1 / span 2;
        margin-top: 0.875rem;
        width: 100%;

        ${MQ.FROM_M} {
            grid-column: ${theme('displayOption', {
                Full: 'auto / span 6',
                Half: 'auto / span 3',
                OneThird: 'auto / span 2',
                TwoThirds: 'auto / span 4',
            })};
            margin-top: 1.3125rem;
        }

        ${MQ.FROM_XL} {
            margin-top: 1.75rem;
        }

        &:first-child {
            margin-top: 0;
        }
    }
`;

export const LabelWrapper = styled.div`
    margin-bottom: 1.75rem;
`;

export const Label = styled(FormLabel)`
    && {
        ${styleLabelM}
        color: var(${textColor});

        &.Mui-focused {
            color: var(${textColor});
        }
    }
`;

export const CheckboxItems = styled(FormGroup)`
    && {
        row-gap: 1.3125rem;
    }
`;

export const IconWrapper = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 0.4375rem;
`;

export const CheckboxIcon = styled.div.attrs<{
    $checked: boolean;
    $error: boolean;
}>(() => ({}))`
    border: 0.0625rem solid var(${borderColor});
    border-radius: 0.25rem;
    display: block;
    height: 1.75rem;
    position: relative;
    transition: border-color 0.2s ease-in-out;
    width: 1.75rem;

    ${MQ.FROM_XL} {
        width: ${theme('size', {
            small: '1.75rem',
            medium: '2.1875rem',
        })};
        height: ${theme('size', {
            small: '1.75rem',
            medium: '2.1875rem',
        })};
    }

    &::before,
    &::after {
        display: none;
        position: absolute;
        border-radius: 0.1875rem;
        background: transparent;
        content: '';
    }

    &::before {
        transition: inset 0.1s ease-in-out;
        background: var(${markedColor});
    }

    ${(props) =>
        props.$error &&
        css`
            border-color: var(${borderError});
        `}

    ${(props) =>
        props.$checked &&
        css`
            &::before {
                display: block;
                inset: 0.4375rem;
            }
        `}
`;

export const ErrorIcon = styled(Icon)`
    color: var(${borderError});
`;

export const CheckboxLabel = styled(FormControlLabel)`
    && {
        align-items: flex-start;
        margin-left: 0;

        .MuiFormControlLabel-label {
            ${theme('size', {
                small: styleBodyM,
                medium: styleBodyL,
            })};
            color: var(${textColor});

            &.Mui-disabled {
                color: var(${disabledColor});
            }
        }

        .MuiFormControlLabel-asterisk {
            display: none;
        }

        &:hover:not(.Mui-disabled) {
            ${CheckboxIcon} {
                border-color: var(${borderHover});

                &::before {
                    display: block;
                    inset: 0.6875rem;
                    border-radius: 0.125rem;

                    ${MQ.FROM_XL} {
                        inset: ${theme('size', {
                            small: '0.6875rem',
                            medium: '0.875rem',
                        })};
                    }

                    ${(props) =>
                        props.checked &&
                        css`
                            border-radius: 0.125rem;
                            inset: 0.5625rem;

                            ${MQ.FROM_XL} {
                                inset: 0.5625rem;
                                border-radius: ${theme('size', {
                                    small: '0.125rem',
                                    medium: '0.1875rem',
                                })};
                            }
                        `}
                }
            }
        }
    }
`;

export const CheckboxControl = styled(Checkbox)`
    && {
        margin-right: 0.875rem;
        padding: 0;

        &:has(> input:focus) {
            ${CheckboxIcon} {
                box-shadow: 0 0 0 0.4375rem var(--field-focus);
                border-color: var(${borderHover});
            }
        }

        &:hover {
            background: none;
        }

        &.Mui-disabled {
            ${CheckboxIcon} {
                border-color: var(${borderDisabledColor});

                &::before {
                    background-color: var(${disabledColor});
                }
            }
        }

        &.Mui-error {
            ${CheckboxIcon} {
                border-color: var(${borderError});

                &::before {
                    background-color: var(${borderError});
                }
            }
        }
    }
`;

export const Description = styled(FormHelperText)`
    && {
        ${theme('size', {
            small: styleBodyS,
            medium: styleBodyM,
        })};
        margin-left: 0;
        color: var(${labelColor});
        letter-spacing: normal;

        &.Mui-disabled {
            color: var(${disabledColor});
        }
    }
`;

export const HelperText = styled(FormHelperText)`
    && {
        ${theme('size', {
            small: styleBodyS,
            medium: styleBodyM,
        })};
        margin: 0;
        color: var(${labelColor});
        letter-spacing: normal;
        text-transform: none;

        &.Mui-disabled {
            color: var(${disabledColor});
        }
    }
`;
